// client/src/components/history/PublicEventArticle.jsx
//
// Read-only public "article" for one event: name as the header, an optional
// intro paragraph, then the event's public media in chronological order of
// being made public — each with its caption and (if the tenant allows)
// likes / comments. Media taps open the full-screen viewer.

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import MediaViewer from '../media/MediaViewer';

const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

const MEMBER_TOKEN_KEY = 'memberToken';
const MEMBER_KEY = 'memberInfo';
const getMemberToken = () => localStorage.getItem(MEMBER_TOKEN_KEY);
const clearMemberSession = () => { localStorage.removeItem(MEMBER_TOKEN_KEY); localStorage.removeItem(MEMBER_KEY); };
const memberHeaders = () => {
  const t = getMemberToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const pageStyle = { minHeight: '100vh', backgroundColor: '#1a1a2e', color: '#ffffff', padding: '2rem 1rem' };
const isVideoMime = (m) => /^video\//i.test(m || '');

export default function PublicEventArticle() {
  const { tenant, eventId } = useParams();
  const navigate = useNavigate();
  const tenantNum = Number(tenant) || null;

  const [gate, setGate] = useState('loading'); // loading | ok
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [eventName, setEventName] = useState('');
  const [intro, setIntro] = useState(null);
  const [items, setItems] = useState([]);
  const [allowLikes, setAllowLikes] = useState(true);
  const [allowComments, setAllowComments] = useState(true);

  const [mediaViewer, setMediaViewer] = useState(null);

  // ── Gate: reuse the tenant's public-history config ─────────────────────
  useEffect(() => {
    if (!tenantNum) return;
    (async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/events/${tenantNum}/publicHistoryConfig`);
        if (res.data?.success) {
          if (res.data.requireLogin && !getMemberToken()) {
            navigate(`/${tenant}/memberlogin`, { replace: true });
            return;
          }
          setGate('ok');
        } else {
          setErr(res.data?.message || 'Event history is not available.');
          setGate('ok');
        }
      } catch (e) {
        setErr(e.response?.status === 403
          ? 'This event history is not publicly available.'
          : 'Failed to load event history.');
        setGate('ok');
      }
    })();
  }, [tenantNum]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchArticle = useCallback(async () => {
    if (!tenantNum) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await axios.get(
        `${baseUrl}/api/events/${tenantNum}/publicHistory/events/${eventId}`,
        { headers: memberHeaders() }
      );
      if (res.data?.success) {
        setEventName(res.data.eventName || '');
        setIntro(res.data.intro || null);
        setItems(res.data.items || []);
        if (typeof res.data.allowLikes === 'boolean') setAllowLikes(res.data.allowLikes);
        if (typeof res.data.allowComments === 'boolean') setAllowComments(res.data.allowComments);
      } else {
        setErr(res.data?.message || 'Event history is not available.');
      }
    } catch (e) {
      const status = e.response?.status;
      const data = e.response?.data;
      if (status === 401 && data?.requireLogin) {
        clearMemberSession();
        navigate(`/${tenant}/memberlogin`, { replace: true });
      } else if (status === 402 && data?.requirePayment) {
        navigate(`/${tenant}/publicEventHistory`, { replace: true });
      } else if (status === 404) {
        setErr('This event is not available.');
      } else {
        setErr('Failed to load event history.');
      }
    } finally {
      setLoading(false);
    }
  }, [tenantNum, eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (gate === 'ok') fetchArticle(); }, [gate, fetchArticle]);

  const handleToggleLike = async (fileId) => {
    if (!getMemberToken()) { navigate(`/${tenant}/memberlogin`); return; }
    setItems((prev) => prev.map((it) => {
      if (it.fileId !== fileId) return it;
      const was = !!it.viewerLiked;
      return { ...it, viewerLiked: !was, likeCount: Math.max(0, (Number(it.likeCount) || 0) + (was ? -1 : 1)) };
    }));
    try {
      const res = await axios.post(
        `${baseUrl}/api/members/${tenantNum}/public-history/files/${fileId}/like`,
        {}, { headers: memberHeaders() }
      );
      if (res.data?.success) {
        setItems((prev) => prev.map((it) => it.fileId === fileId
          ? { ...it, viewerLiked: !!res.data.liked, likeCount: Number(res.data.likeCount) || 0 }
          : it));
      }
    } catch (e) {
      if (e.response?.status === 401) { clearMemberSession(); navigate(`/${tenant}/memberlogin`); }
    }
  };

  if (gate === 'loading') {
    return <div style={pageStyle}><p style={{ textAlign: 'center', color: '#aaa' }}>Loading...</p></div>;
  }

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <button
          type="button"
          onClick={() => navigate(`/${tenant}/publicEventHistory`)}
          style={{ background: 'transparent', border: 'none', color: '#ff6800', cursor: 'pointer', padding: 0, marginBottom: '1rem', fontSize: '0.9rem' }}
        >
          ← All events
        </button>

        {loading && <p style={{ textAlign: 'center', color: '#aaa' }}>Loading...</p>}

        {err && (
          <div style={{ textAlign: 'center', padding: '2rem', backgroundColor: '#16213e', borderRadius: 8, color: '#ff6800' }}>
            {err}
          </div>
        )}

        {!loading && !err && (
          <article>
            <h1 style={{ color: '#ff6800', marginTop: 0 }}>{eventName}</h1>

            {intro && (
              <div style={{ marginBottom: '2rem', lineHeight: 1.6, color: '#e6e6e6' }}>
                {String(intro).split(/\n{2,}/).map((para, i) => (
                  <p key={i} style={{ whiteSpace: 'pre-wrap', margin: '0 0 1rem' }}>{para}</p>
                ))}
              </div>
            )}

            {items.length === 0 && (
              <p style={{ textAlign: 'center', color: '#aaa' }}>Nothing has been published for this event yet.</p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {items.map((it) => (
                <PublicMediaCard
                  key={it.fileId}
                  item={it}
                  tenantNum={tenantNum}
                  tenant={tenant}
                  allowLikes={allowLikes}
                  allowComments={allowComments}
                  canPost={!!getMemberToken()}
                  onExpand={() => setMediaViewer({ url: it.url, isVideo: isVideoMime(it.mimetype) })}
                  onToggleLike={() => handleToggleLike(it.fileId)}
                  onSignInClick={() => navigate(`/${tenant}/memberlogin`)}
                />
              ))}
            </div>
          </article>
        )}

        {mediaViewer && (
          <MediaViewer src={mediaViewer.url} isVideo={mediaViewer.isVideo} onClose={() => setMediaViewer(null)} />
        )}

        <div style={{ textAlign: 'center', marginTop: '3rem', paddingTop: '1.5rem', borderTop: '1px solid #333', color: '#666', fontSize: '0.85rem' }}>
          Powered by{' '}
          <a href="https://on-screen.app" target="_blank" rel="noopener noreferrer" style={{ color: '#ff6800', textDecoration: 'none' }}>
            On-Screen
          </a>
        </div>
      </div>
    </div>
  );
}

function PublicMediaCard({
  item, tenantNum, allowLikes, allowComments, canPost,
  onExpand, onToggleLike, onSignInClick,
}) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const loadComments = useCallback(async () => {
    try {
      const res = await axios.get(
        `${baseUrl}/api/members/${tenantNum}/public-history/files/${item.fileId}/comments`
      );
      if (res.data?.success) setComments(res.data.comments || []);
    } catch { setComments([]); }
  }, [tenantNum, item.fileId]);

  const toggleComments = async () => {
    const willOpen = !showComments;
    setShowComments(willOpen);
    if (willOpen && comments == null) await loadComments();
  };

  const submitComment = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    if (!canPost) { onSignInClick(); return; }
    setBusy(true);
    try {
      const res = await axios.post(
        `${baseUrl}/api/members/${tenantNum}/public-history/files/${item.fileId}/comments`,
        { body }, { headers: memberHeaders() }
      );
      if (res.data?.success && res.data.comment) {
        setComments((prev) => [...(prev || []), res.data.comment]);
        setDraft('');
      }
    } catch { /* ignore — resyncs on reload */ } finally { setBusy(false); }
  };

  const isVideo = isVideoMime(item.mimetype);

  return (
    <div style={{ backgroundColor: '#16213e', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.35)' }}>
      <div
        role="button"
        tabIndex={0}
        onClick={onExpand}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onExpand()}
        style={{ cursor: 'pointer', background: '#0f0f1a', display: 'flex', justifyContent: 'center' }}
      >
        {isVideo ? (
          <video src={item.url} muted playsInline preload="metadata" style={{ maxWidth: '100%', maxHeight: 520, objectFit: 'contain' }} />
        ) : (
          <img src={item.url} alt={item.caption || 'event media'} loading="lazy" style={{ maxWidth: '100%', maxHeight: 520, objectFit: 'contain' }} />
        )}
      </div>

      {(item.caption || allowLikes || allowComments) && (
        <div style={{ padding: '1rem' }}>
          {item.caption && (
            <p style={{ whiteSpace: 'pre-wrap', margin: 0, lineHeight: 1.55, color: '#e6e6e6' }}>{item.caption}</p>
          )}

          {(allowLikes || allowComments) && (
            <div style={{ display: 'flex', gap: 16, marginTop: item.caption ? '0.75rem' : 0, paddingTop: '0.5rem', borderTop: '1px solid #2a2a40' }}>
              {allowLikes && (
                <button
                  type="button"
                  onClick={onToggleLike}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: item.viewerLiked ? '#ff4d6d' : '#cbd5e1' }}
                >
                  <span style={{ fontSize: '1rem' }}>{item.viewerLiked ? '♥' : '♡'}</span>
                  <span>{Number(item.likeCount) || 0}</span>
                </button>
              )}
              {allowComments && (
                <button
                  type="button"
                  onClick={toggleComments}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: '#cbd5e1' }}
                >
                  <span style={{ fontSize: '1rem' }}>💬</span>
                  <span>{Number(item.commentCount) || 0}</span>
                </button>
              )}
            </div>
          )}

          {allowComments && showComments && (
            <div style={{ marginTop: '0.75rem' }}>
              {(comments || []).length === 0 ? (
                <p style={{ fontSize: '0.85rem', color: '#888', margin: '0 0 0.5rem' }}>No comments yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '0.5rem' }}>
                  {comments.map((c) => (
                    <div key={c.id} style={{ backgroundColor: '#0f0f1a', padding: '6px 8px', borderRadius: 6, fontSize: '0.85rem' }}>
                      <strong style={{ color: '#ff6800', fontSize: '0.8rem' }}>{c.authorName || 'Member'}</strong>
                      <div style={{ color: '#cbd5e1', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.body}</div>
                    </div>
                  ))}
                </div>
              )}
              {canPost ? (
                <form onSubmit={submitComment} style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Add a comment…"
                    disabled={busy}
                    maxLength={2000}
                    style={{ flex: 1, padding: '6px 8px', borderRadius: 4, border: '1px solid #333', backgroundColor: '#0f0f1a', color: '#fff', fontSize: '0.85rem' }}
                  />
                  <button type="submit" disabled={busy || !draft.trim()} style={{ padding: '6px 10px', borderRadius: 4, border: 'none', backgroundColor: '#ff6800', color: '#fff', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', opacity: busy || !draft.trim() ? 0.6 : 1 }}>
                    Post
                  </button>
                </form>
              ) : (
                <button
                  type="button"
                  onClick={onSignInClick}
                  style={{ background: 'none', border: '1px solid #ff6800', color: '#ff6800', padding: '6px 10px', borderRadius: 4, fontSize: '0.8rem', cursor: 'pointer', width: '100%' }}
                >
                  Sign in to comment
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
